// lista de clientes

import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Paper,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from '@mui/icons-material/Download';
import ClientForm from './ClientForm';
import jsPDF from "jspdf";
import { apiFetch } from '../fetchWrapper';
import autoTable from "jspdf-autotable";
import { useUser } from '../UserContext';

export default function ClientList({ clients, onSaveClient, onDeleteClient, onSelect }) {

  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [search, setSearch] = useState('');
  const { user, hasRole } = useUser();

  const handleEdit = async (client) => {
    setEditingClient(client);
    setShowClientForm(true);
  }

  const handleAdd = async () => {
    setShowClientForm(true);
    setEditingClient(null)
  }

  const handleSaveClient = (savedClient) => {
    setShowClientForm(false);
    onSaveClient(savedClient);
  };

  const filteredClients = clients.filter(client => {
    const fullAddress = `${client.address.street} ${client.address.city} ${client.address.state}`.toLowerCase();
    return fullAddress.includes(search.toLowerCase()) || client.companyName.toLowerCase().includes(search.toLowerCase());
  });

  const handleDelete = async (id) => {
    const confirmed = window.confirm('¿Desea eliminar este cliente?');
    if (!confirmed) return;

    try {
      const res = await apiFetch(`/api/clients/${id}`, {
        method: 'DELETE',
      });

      if (!res) return;

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      onDeleteClient(id);
      alert('Cliente eliminado');
    } catch (err) {
      console.error(err);
      alert('Error al eliminar el cliente');
    }
  };

  const handleCancelForm = async () => {
    setShowClientForm(false);
    setEditingClient(null);
  }

  const handleExport = async () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Listado de Clientes", 14, 22);

    const tableColumn = ["Nombre", "Calle", "Ciudad", "Código Postal", "Estado"];
    const tableRows = filteredClients.map((client) => [
      client.companyName || '-',
      client.address?.street || '-',
      client.address?.city || '-',
      client.address?.zipCode || '-',
      client.address?.state || '-'
    ]);

    autoTable(doc, {
      startY: 30,
      head: [tableColumn],
      body: tableRows,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { fontSize: 10 },
    });

    // Guarda PDF
    doc.save("clientes.pdf");
  }

  const handleExportCsv = () => {
    const csvHeaders = [
      'Cliente',
      'Contacto',
      'Teléfono',
      'Teléfono Móvil',
      'RFC',
      'Estado',
    ];

    const csvRows = filteredClients.map((client) => {
      const contactName = `${client.contactPerson?.firstname || ''} ${client.contactPerson?.lastnameFirst || ''}`.trim();
      return [
        client.companyName || '-',
        contactName || '-',
        client.contactPerson?.phoneNumber || '-',
        client.contactPerson?.mobileNumber || '-',
        client.rfc || '-',
        client.address?.state || '-',
      ];
    });

    const csvContent = [csvHeaders, ...csvRows]
      .map((row) => row
        .map((value) => `"${String(value || '').replace(/"/g, '""')}"`)
        .join(','))
      .join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'clientes.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  return (
    <>
      {showClientForm && (
        <>
          <ClientForm client={editingClient} onCancel={handleCancelForm} onSave={handleSaveClient} />
          <br />
        </>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mr: 1 }}>
        <Typography variant="h6">Registro de Cliente</Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flexDirection: { xs: 'column', sm: 'row' },
          fontSize: { xs: 10, sm: 12 },
          px: 1,      
          }}>

            {hasRole("ADMIN", "HR_ADMIN") && (  

          <Button sx={{
            backgroundColor: "#3f74b1",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#364f6b"
            }
          }}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}>
            Agregar Cliente
          </Button>
            )}

          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExport}>
            Exportar PDF
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}>
            Exportar CSV
          </Button>

        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ p: 2, width: "100%", overflowX: 'auto' }}>
        <TextField
          label="Buscar cliente"
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Table sx={{ minWidth: 650 }}>
          <TableHead
            sx={{
              backgroundColor: '#f5f5f5',
              textTransform: 'uppercase',
            }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Contacto</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Teléfono</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>TeléfonoMóvil</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>RFC</TableCell>
              <TableCell sx={{ fontWeight: "bold", display: { xs: 'none', md: 'table-cell' } }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClients.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No se encontraron resultados
                </TableCell>
              </TableRow>
            )}
            {(filteredClients).map((client) => (
              <TableRow key={client.id}
                hover
                sx={{
                  "&:hover .MuiTableCell-root": {
                    backgroundColor: "action.hover",
                  },
                }}
              >

                <TableCell sx={{
                  maxWidth: 180,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                >
                  {client.companyName}</TableCell>
                <TableCell>{client.contactPerson.firstname} {client.contactPerson.lastnameFirst}</TableCell>
                <TableCell>{client.contactPerson.phoneNumber}</TableCell> 
                <TableCell>{client.contactPerson.mobileNumber}</TableCell> 
                <TableCell>{client.rfc}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {client.address.state}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="#415A77"
                    size="small"
                    sx={{
                      mr: 1,
                      fontSize: { xs: 10, sm: 12 },
                      px: 1,
                      backgroundColor: "#3f74b1",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#364f6b"
                      }
                    }}
                    onClick={() => onSelect(client)}
                  >
                    Ver detalle
                  </Button>

                   {hasRole("ADMIN", "HR_ADMIN") && (

                  <Tooltip title="Editar"
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px'
                        }
                      }
                    }}
                  >
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(client)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  )}


                  {hasRole("ADMIN", "HR_ADMIN") && (

                  <Tooltip title="Borrar"
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px'
                        }
                      }
                    }}
                  >
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(client.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                  )}

                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* </Paper> */}
    </>
    // </Box>
  );
}